import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSWRInfinite } from "swr";
import type { WithPagination } from "../../../../types/types";
import type { ActivityTargetType } from "../../../../util/activityEndpoints";
import type { ActivityHistoryItem } from "./types";
import {
  activitiesHasMoreAfterPage,
  fetchActivities,
} from "./activityPagination";

interface UseActivitiesArgs {
  tenantId?: string;
  targetId?: string;
  targetType?: ActivityTargetType;
  enabled: boolean;
  includeDetails: boolean;
  versionNumber?: number | null;
}

export function useActivities({
  tenantId,
  targetId,
  targetType,
  enabled,
  includeDetails,
  versionNumber,
}: UseActivitiesArgs) {
  const lastLoadPageRef = useRef(0);

  const getKey = useMemo(
    () =>
      fetchActivities({
        tenantId,
        targetId,
        targetType,
        enabled,
        includeDetails,
        versionNumber,
      }),
    [tenantId, targetId, targetType, enabled, includeDetails, versionNumber]
  );

  const { data, error, isValidating, size, setSize } = useSWRInfinite<
    WithPagination<{ data: ActivityHistoryItem[] }>
  >(getKey, {
    revalidateOnFocus: false,
    dedupingInterval: 3000,
  });

  useEffect(() => {
    lastLoadPageRef.current = 0;
  }, [getKey]);

  const { pages, items } = useMemo(() => {
    const pages = Array.isArray(data) ? data : [];
    return {
      pages,
      items: pages.flatMap((page) => page?.data || []),
    };
  }, [data]);

  const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;
  const hasMore = activitiesHasMoreAfterPage(lastPage);

  const loadMore = useCallback(() => {
    if (isValidating) return;
    if (lastLoadPageRef.current >= size) return;
    if (!activitiesHasMoreAfterPage(lastPage)) return;
    lastLoadPageRef.current = size;
    void setSize(size + 1);
  }, [isValidating, lastPage, setSize, size]);

  return {
    items,
    pages,
    error,
    isValidating,
    hasMore,
    loadMore,
  };
}
